import { MENU_FOOTER_SOURCE } from '../analytics.ts';

export const PROMO_BANNER_ACTION_SUBJECT_ID = 'connectionsBylinePromoBanner';

export const GDRIVE_ANALYTICS_CONNECTOR = 'google';

export type PromoBannerType = 'gdrivePromo' | 'orgConnectedApps';

export type OrgBannerVariant = 'highAdoption' | 'lowAdoption';

export type GdriveIneligibleReason =
	| 'notFirstPartyOnly'
	| 'noSiteWideDriveLinks'
	| 'alreadyAuthenticated'
	| 'dismissed'
	| 'noAuthUrl';

export type OrgIneligibleReason = 'noUnconnectedThirdParty' | 'noTopApp' | 'dismissed';

type GdriveAttributes = {
	bannerType: 'gdrivePromo';
	connector: string;
};

type OrgAttributes = {
	bannerType: 'orgConnectedApps';
	// `connector` is the smartlink provider-family key (`meta.key` minus `-object-provider`,
	// e.g. `google`). The gdrive banner reports `google` to align with the org banner's trait
	// keys, not the `google-drive`/`drive` keys it uses internally.
	connector: string;
	variant: OrgBannerVariant;
	topAppPercent: number;
};

export type PromoBannerAttributes = GdriveAttributes | OrgAttributes;

export const getBannerShownEvent = (
	attributes: PromoBannerAttributes,
): {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: 'viewed';
		readonly actionSubject: 'banner';
		readonly actionSubjectId: 'connectionsBylinePromoBanner';
		readonly source: 'connectionsBylineMenuFooter';
		readonly attributes: PromoBannerAttributes;
	};
} =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'viewed',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MENU_FOOTER_SOURCE,
			attributes,
		},
	}) as const;

export const getBannerDismissedEvent = (
	attributes: PromoBannerAttributes,
): {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: 'dismissed';
		readonly actionSubject: 'banner';
		readonly actionSubjectId: 'connectionsBylinePromoBanner';
		readonly source: 'connectionsBylineMenuFooter';
		readonly attributes: PromoBannerAttributes;
	};
} =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'dismissed',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MENU_FOOTER_SOURCE,
			attributes,
		},
	}) as const;

export type BannerIneligibleAttributes = {
	gdriveIneligibleReason?: GdriveIneligibleReason;
	orgConnectedAppsIneligibleReason?: OrgIneligibleReason;
};

export const getBannerNotShownEvent = (
	attributes: BannerIneligibleAttributes,
): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'notShown';
		readonly actionSubject: 'banner';
		readonly actionSubjectId: 'connectionsBylinePromoBanner';
		readonly source: 'connectionsBylineMenuFooter';
		readonly attributes: BannerIneligibleAttributes;
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'notShown',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MENU_FOOTER_SOURCE,
			attributes,
		},
	}) as const;

export const GDRIVE_CONNECT_BUTTON_ACTION_SUBJECT_ID = 'connectionsBylineGdrivePromoConnect';
export const GDRIVE_AUTH_ACTION_SUBJECT_ID = 'connectionsBylineGdrivePromoAuth';

export const getGdriveConnectClickedEvent = (): {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: 'clicked';
		readonly actionSubject: 'button';
		readonly actionSubjectId: 'connectionsBylineGdrivePromoConnect';
		readonly source: 'connectionsBylineMenuFooter';
		readonly attributes: {
			readonly bannerType: 'gdrivePromo';
			readonly connector: 'google';
		};
	};
} =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: GDRIVE_CONNECT_BUTTON_ACTION_SUBJECT_ID,
			source: MENU_FOOTER_SOURCE,
			attributes: {
				bannerType: 'gdrivePromo',
				connector: GDRIVE_ANALYTICS_CONNECTOR,
			},
		},
	}) as const;

export const getGdriveConnectSucceededEvent = (): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'succeeded';
		readonly actionSubject: 'auth';
		readonly actionSubjectId: 'connectionsBylineGdrivePromoAuth';
		readonly source: 'connectionsBylineMenuFooter';
		readonly attributes: {
			readonly bannerType: 'gdrivePromo';
			readonly connector: 'google';
			readonly status: 'success';
		};
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'succeeded',
			actionSubject: 'auth',
			actionSubjectId: GDRIVE_AUTH_ACTION_SUBJECT_ID,
			source: MENU_FOOTER_SOURCE,
			attributes: {
				bannerType: 'gdrivePromo',
				connector: GDRIVE_ANALYTICS_CONNECTOR,
				status: 'success',
			},
		},
	}) as const;

export const getGdriveConnectFailedEvent = (
	reason: string | null,
): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'failed';
		readonly actionSubject: 'auth';
		readonly actionSubjectId: 'connectionsBylineGdrivePromoAuth';
		readonly source: 'connectionsBylineMenuFooter';
		readonly attributes: {
			readonly bannerType: 'gdrivePromo';
			readonly connector: 'google';
			readonly status: 'failed';
			readonly reason: string | null;
		};
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'failed',
			actionSubject: 'auth',
			actionSubjectId: GDRIVE_AUTH_ACTION_SUBJECT_ID,
			source: MENU_FOOTER_SOURCE,
			attributes: {
				bannerType: 'gdrivePromo',
				connector: GDRIVE_ANALYTICS_CONNECTOR,
				status: 'failed',
				reason,
			},
		},
	}) as const;
